<script setup>
import i18next from 'i18next';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import SingleTrade from '../components/dashboard/trades/SingleTrade.vue';
import TradeMetrics from '../components/dashboard/trades/TradeMetrics.vue';
import Dropdown from '../components/Dropdown.vue';
import Sheet from '../components/Sheet.vue';
import { fetchData } from '../fetchData';

const lastPage = ref(1);
const page = ref(1);
const trades = ref([]);
const totalTrades = ref(0);
const appendToList = ref(false);
const searchPhrase = ref('');
const directionValues = ['Long', 'Short'];
const tradesMarketFilter = ref([]);
const tradesMarketsSymbolFilterArgs = ref([]);
const tradesMarketFilterQuery = ref('');
const statusValues = computed(() => [
    { key: 'open', title: i18next.t('dashboard_portfolio_filter_states_active_trade') },
    { key: 'closed', title: i18next.t('dashboard_portfolio_filter_states_finished_trade') }
]);

const direction = ref('');
const status = ref('');
const pending = ref(false);
const sentinel = ref(null);
const observer = ref(null);
const loadNextPage = async () => {
    page.value += 1;
    appendToList.value = true;
    await getData();
};
const createObserver = () => {
    if (observer.value) observer.value.disconnect();

    observer.value = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting && page.value < lastPage.value) {
                loadNextPage();
            }
        });
    });

    if (sentinel.value) {
        observer.value.observe(sentinel.value);
    }
};

onBeforeUnmount(() => {
    if (observer.value) {
        observer.value.disconnect();
    }
});

const getTradesMarketFilter = async () => {
    try {
        const { result = [] } = await fetchData('/api/v1/trades/filters');

        tradesMarketFilter.value = result.status_map ?? {};

        tradesMarketFilter.value = result.subscribed_abo_product_symbols_map ?? [];
    } catch (error) {
        console.error('Error fetching price targets:', error);
    }
};
const getData = async () => {
    const API = `/api/v1/trades`;
    try {
        let url = `${API}?sort[by]=entry_time&sort[order]=desc&subscribed_abos=true&tradeType=trade&page_size=200&page=${page.value}`;

        if (searchPhrase.value) {
            url += `&search_phrase=${encodeURIComponent(searchPhrase.value)}`;
        }

        if (direction.value) {
            url += `&direction[]=${encodeURIComponent(direction.value)}`;
        }

        if (status.value) {
            url += `&show_by_status[]=${encodeURIComponent(status.value)}`;
        }

        if (tradesMarketsSymbolFilterArgs.value.length > 0) {
            tradesMarketsSymbolFilterArgs.value.forEach((symbolId) => {
                url += `&symbol_id[]=${symbolId}`;
            });
        }

        const { result = [] } = await fetchData(url);

        if (appendToList.value) {
            trades.value = [...trades.value, ...result.data];
        } else {
            trades.value = result.data;
        }

        totalTrades.value = result.meta?.total || 0;
        page.value = result.meta?.current_page || 1;
        lastPage.value = result.meta?.last_page || 1;
        await nextTick();
        window.dispatchEvent(new Event('resize'));
    } catch (e) {
        console.error('Error fetching data:', e);
    }
};
onMounted(async () => {
    pending.value = true;
    await getTradesMarketFilter();
    await getData();
    pending.value = false;
    createObserver();
});

const debounce = (func, delay) => {
    let timeout;

    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };
};

const handleSearchInput = debounce((value) => {
    searchPhrase.value = value;
    appendToList.value = false;
    getData();
}, 300);

const isAllSelected = (value) => value === null || value === 'Alle' || value === 'All';
const handleDropdownDirectionChange = (value) => {
    if (isAllSelected(value)) {
        direction.value = '';

        return getData();
    }

    direction.value = value;
    getData();
};

const handleDropdownMarketChange = (value) => {
    if (isAllSelected(value)) {
        tradesMarketFilterQuery.value = '';
        tradesMarketsSymbolFilterArgs.value = [];

        return getData();
    }

    tradesMarketFilterQuery.value = value;
    const market = tradesMarketFilter.value.find((item) => item?.product_id === value);
    tradesMarketsSymbolFilterArgs.value = market?.symbols && typeof market.symbols === 'object' ? Object.keys(market.symbols) : [];
    getData();
};
const handleDropdownStatusChange = (value) => {
    if (isAllSelected(value)) {
        status.value = '';

        return getData();
    }

    status.value = value;
    getData();
};

const resetFilters = () => {
    searchPhrase.value = '';
    direction.value = '';
    status.value = '';
    appendToList.value = false;
    tradesMarketFilterQuery.value = '';
    tradesMarketsSymbolFilterArgs.value = [];
    page.value = 1;
    getData();
};

const clearSearch = () => {
    searchPhrase.value = '';
    page.value = 1;
    getData();
};
</script>

<template>
    <div class="h-full w-full lg:px-20 md:px-10 sm:px-2 bg-[#F1F6FA]h-screen">
        <div class="mx-auto container lg:p-4 flex flex-col gap-4">
            <TradeMetrics :trades="trades" :total-trades="totalTrades" />
            <Sheet compact>
                <div class="my-2">
                    <span class="font-bold text-base">Trades</span>
                </div>
                <div class="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 lg:gap-12 mb-4 h-full">
                    <div class="relative w-full lg:max-w-[370px]">
                        <input
                            v-model="searchPhrase"
                            type="text"
                            :placeholder="$t('plain_search')"
                            class="block w-full px-3 py-2 border rounded-lg bg-dashboard-primary shadow-sm hover:shadow-md focus:outline-none text-xs duration-300"
                            @input="handleSearchInput($event.target.value)"
                        />
                        <i v-if="!searchPhrase" class="fi fi-rr-search absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-400"></i>
                        <i v-else class="fi fi-rr-cross-circle absolute top-1/2 right-3 transform -translate-y-1/2 cursor-pointer hover:text-gray-600" @click="clearSearch"></i>
                    </div>
                    <div class="flex flex-col lg:flex-row gap-2 flex-auto lg:w-1/2">
                        <Dropdown
                            :dropdown-values="directionValues.map((value) => ({ key: value.toLowerCase(), title: value }))"
                            :selected-dropdown-value="direction"
                            filter-label="Long/Short"
                            class="w-full"
                            @update:selected-dropdown-value="handleDropdownDirectionChange"
                        />
                        <Dropdown
                            v-if="tradesMarketFilter && tradesMarketFilter.length > 1"
                            :dropdown-values="tradesMarketFilter.map((value) => ({ key: value.product_id, title: value?.product_name }))"
                            :selected-dropdown-value="tradesMarketFilterQuery"
                            :filter-label="$t('all_markets')"
                            class="ring-2 ring-blue/50"
                            @update:selected-dropdown-value="handleDropdownMarketChange"
                        />
                        <Dropdown
                            :dropdown-values="statusValues"
                            :selected-dropdown-value="status"
                            :filter-label="$t('dashboard_portfolio_filter_states_all_trades')"
                            class="w-full"
                            @update:selected-dropdown-value="handleDropdownStatusChange"
                        />
                    </div>
                </div>
                <no-item-found v-if="tradesMarketFilter.length === 0 || pending" class="h-[370px]" :item-name="$t('plain_trades')" />
                <div v-if="!pending" class="relative">
                    <div data-scroll-container class="overflow-y-scroll h-full pb-14 scroll-smooth max-h-[60vh]">
                        <SingleTrade :trades="trades" />

                        <div v-if="(trades?.length || 0) === 0" class="text-center mt-4">
                            <button class="px-4 py-2 border border-blue text-blue rounded-lg hover:bg-blue/70 hover:text-white transition duration-300" @click="resetFilters">
                                {{ $t('reset_filters') }}
                            </button>
                        </div>

                        <div ref="sentinel" class="h-12 mt-4 flex justify-center items-center text-gray-400">
                            <span v-if="page < lastPage">{{ $t('loading') }}...</span>
                        </div>
                    </div>
                </div>
            </Sheet>
        </div>
    </div>
</template>
